// ============================================================================
// BROWSER VOICE AGENT
// Holds a two-way ElevenLabs conversation with one avatar in this tab. The
// robot path does the same thing on the device; this one needs no hardware.
//
// Must be called inside an <ElevenLabsConversationProvider> — the SDK keeps
// conversation state in React context, so the hook cannot stand alone.
// ============================================================================

import { useCallback, useRef, useState } from 'react';
import { useConversation } from '@elevenlabs/react';
import type { MessagePayload } from '@elevenlabs/client';
import { api } from '../services/api';

export type BrowserVoiceStatus = 'idle' | 'connecting' | 'active' | 'error';

interface BrowserTokenResponse {
  conversationToken: string;
  conversationId: string;
  ttsVoiceId: string;
  openingLine: string | null;
  sessionToken: string;
  dynamicVariables: Record<string, string>;
}

export interface StartOptions {
  /** Extra steer for this conversation, appended to the avatar's persona. */
  context?: string;
  /** What the avatar says first, instead of its configured greeting. */
  openingLine?: string;
}

export function useBrowserVoiceAgent(sessionId: string) {
  const [status, setStatus] = useState<BrowserVoiceStatus>('idle');
  const [avatarRole, setAvatarRole] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUserText, setLastUserText] = useState<string | null>(null);
  const [lastAgentText, setLastAgentText] = useState<string | null>(null);

  // The conversation is minted server-side, so the id and the HMAC token that
  // scopes the agent's tool calls are known before the socket opens. The role
  // lives here too, so the disconnect handler does not read stale state.
  const session = useRef<{ conversationId: string; sessionToken: string; role: string } | null>(null);

  // Every turn as it arrives. The backend writes these straight into the
  // session transcript, so ElevenLabs' stored copy is never re-fetched.
  const turns = useRef<Array<{ role: string; text: string; timestamp: string }>>([]);

  /**
   * Ends a conversation however it stopped: the facilitator pressing the
   * button, or the agent hanging up when asked to. Both have to clear the UI
   * and forward the transcript, so neither path is allowed to own that.
   * Clearing the ref first makes a second call a no-op, which matters because
   * endSession() also triggers onDisconnect.
   */
  const finish = useCallback(async () => {
    const started = session.current;
    session.current = null;
    setStatus('idle');
    setAvatarRole(null);
    if (!started) return;

    try {
      const collected = turns.current;
      turns.current = [];
      await api.post(`/api/sessions/${sessionId}/voice-agent-transcript`, {
        sessionToken: started.sessionToken,
        avatarRole: started.role,
        turns: collected,
      });
    } catch {
      // The conversation itself succeeded; losing the ingest callback should
      // not surface as a failed call. The backend logs it.
    }
  }, [sessionId]);

  const conversation = useConversation({
    onConnect: () => setStatus('active'),
    onDisconnect: () => { void finish(); },
    onError: (message: string) => {
      setError(message || 'The conversation ended unexpectedly.');
      setStatus('error');
    },
    // `role` rather than the deprecated `source`.
    onMessage: ({ message, role }: MessagePayload) => {
      if (!message?.trim()) return;
      turns.current.push({ role, text: message, timestamp: new Date().toISOString() });
      if (role === 'user') setLastUserText(message);
      else setLastAgentText(message);
    },
  });

  const start = useCallback(async (role: string, opts: StartOptions = {}) => {
    setError(null);
    setLastUserText(null);
    setLastAgentText(null);
    turns.current = [];
    setAvatarRole(role);
    setStatus('connecting');

    try {
      // Ask first and fail loudly. Letting the SDK request it means a denied
      // mic looks identical to an avatar that simply will not talk.
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('Microphone access is blocked. Allow it for this site and try again.');
      setStatus('error');
      setAvatarRole(null);
      return;
    }

    try {
      const { data } = await api.post<BrowserTokenResponse>(
        `/api/sessions/${sessionId}/avatars/${encodeURIComponent(role)}/voice-agent/browser-token`,
        { context: opts.context, openingLine: opts.openingLine }
      );

      session.current = { conversationId: data.conversationId, sessionToken: data.sessionToken, role };

      // conversationToken implies WebRTC and forbids agentId, so the agent the
      // backend resolved rides inside the token rather than beside it.
      conversation.startSession({
        conversationToken: data.conversationToken,
        dynamicVariables: data.dynamicVariables,
        overrides: {
          ...(data.ttsVoiceId ? { tts: { voiceId: data.ttsVoiceId } } : {}),
          // Omitted entirely when there is no opening line, so the agent falls
          // back to its own configured greeting.
          ...(data.openingLine ? { agent: { firstMessage: data.openingLine } } : {}),
        },
      });
    } catch (err: any) {
      const detail = err?.response?.data?.error;
      setError(detail || 'Could not start the conversation.');
      setStatus('error');
      setAvatarRole(null);
    }
  }, [sessionId, conversation]);

  const stop = useCallback(async () => {
    conversation.endSession();
    await finish();
  }, [conversation, finish]);

  return {
    status,
    avatarRole,
    error,
    lastUserText,
    lastAgentText,
    isSpeaking: conversation.isSpeaking,
    isActive: status === 'connecting' || status === 'active',
    start,
    stop,
  };
}
